import { css } from "@emotion/css";
import type { PageAction } from "@octopusdeploy/design-system-components";
import { EmptyStateNoResults, DeprecatedIconButton, Pagination, SearchInput, SimpleDataTable } from "@octopusdeploy/design-system-components";
import { DeprecatedSelect } from "@octopusdeploy/design-system-octopus-components";
import { useQuery, useMutation } from "@octopusdeploy/octopus-react-client";
import type { ResourceCollection, ApiKeyWithUserResource, ApiKeyResource, Repository } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import { isEqual } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import DeleteApiKeyDialog from "~/areas/users/MyApiKeys/DeleteApiKeyDialog";
import { AdvancedFilterCheckbox } from "~/components/AdvancedFilterLayout";
import { AdvancedFilters } from "~/components/AdvancedFilterLayout/AdvancedFilters";
import { ApiKeyExpiryChip } from "~/components/Chips";
import InternalLink from "~/components/Navigation/InternalLink";
import { PageContent } from "~/components/PageContent/PageContent";
import TimeFromNowLabel from "~/components/TimeLabels/TimeFromNowLabel";
import { useDebouncedExternalState } from "~/hooks/useDebouncedExternalState";
import { useDeepCompareMemo } from "~/hooks/useDeepCompareMemo";
export interface ApiKeysPageProps {
    queryParams: ApiKeysQueryParams;
    setQueryParams: QueryParamValuesSetter<ApiKeysQueryParams>;
    loaderData: ApiKeysPageLoaderData;
}
export interface ApiKeysPageLoaderData {
    apiKeys: ResourceCollection<ApiKeyWithUserResource>;
    hasApiKeys: boolean; // Additional state to deal with filtering scenarios that return no apiKeys data.
}
export interface ApiKeysQueryParams {
    partialPurpose: string;
    neverExpire: boolean;
    hasExpired: boolean;
    willExpireInDays: number | undefined;
    userId: string;
    page: number | undefined;
    pageSize: number | undefined;
}
interface Filter extends ApiKeysQueryParams {
}
export async function apiKeysPageLoader(repository: Repository, queryParams: ApiKeysQueryParams): Promise<ApiKeysPageLoaderData> {
    return getPaginatedApiKeys(repository, queryParams);
}
const PageTitle = "API Keys";
const defaultPageSize = 30;
const pageSizeOptions = [defaultPageSize, 50, 100];
export function ApiKeysPage({ loaderData, queryParams, setQueryParams }: ApiKeysPageProps) {
    const [debouncedApiKeyName, setDebouncedApiKeyName] = useDebouncedExternalState(queryParams.partialPurpose, (value) => setQueryParams({ partialPurpose: value, page: 1 }), 200);
    const defaultFilter = useDefaultFilter();
    const filter = useFilter(defaultFilter, queryParams, setQueryParams);
    const isFiltered = !isEqual(filter, defaultFilter);
    const [apiKeyToDelete, setApiKeyToDelete] = useState<ApiKeyWithUserResource | undefined>(undefined);
    const { result: apiKeysResult, refetch: refretchApiKeys } = usePaginatedApiKeysQuery(loaderData, queryParams);
    const { result: users } = useQuery((repository) => repository.Users.all(), [], "Get all users");
    const { hasApiKeys, apiKeys } = apiKeysResult;
    const onPageChange = useCallback((page: number) => {
        setQueryParams({ ...queryParams, page });
    }, [queryParams, setQueryParams]);
    const onPageSizeChange = useCallback((pageSize: number) => {
        setQueryParams({ ...queryParams, pageSize: pageSize, page: 1 });
    }, [queryParams, setQueryParams]);
    const mask = "\u2022".repeat(8); // Unicode of '•' char
    const maskKey = (apiKey: ApiKeyWithUserResource) => {
        return apiKey.ApiKey.Hint?.trim() ? `(${apiKey.ApiKey.Hint}${mask})` : "";
    };
    const { execute: revokeApiKey } = useMutation({
        name: "Revoke API Key",
        action: async (repository, apiKeyToDelete: ApiKeyWithUserResource) => {
            const apiKey: ApiKeyResource = {
                Id: apiKeyToDelete.Id,
                Purpose: apiKeyToDelete.Purpose,
                UserId: apiKeyToDelete.User.Id,
                ApiKey: apiKeyToDelete.ApiKey,
                Created: apiKeyToDelete.Created,
                Expires: apiKeyToDelete.Expires,
                Links: apiKeyToDelete.Links,
            };
            await repository.Users.revokeApiKey(apiKey);
        },
        afterComplete: async () => {
            refretchApiKeys();
        },
    });
    if (!hasApiKeys) {
        return (<PageContent header={{ title: PageTitle }}>
                <EmptyStateNoResults />
            </PageContent>);
    }
    const hideBottomPagination = apiKeys.Items.length < 20;
    const myApiKeysPageAction: PageAction = {
        type: "navigate",
        buttonType: "secondary",
        label: "My API Keys",
        path: links.myApiKeysPage.generateUrl(),
    };
    return (<PageContent header={{ title: PageTitle, pageActions: [myApiKeysPageAction] }} pagination={{
            ui: (<Pagination label={"API Keys"} totalResults={apiKeys.TotalResults} itemsPerPageOptions={pageSizeOptions} currentPage={queryParams.page ?? 1} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange} selectedItemsPerPage={apiKeys.ItemsPerPage}/>),
            placement: hideBottomPagination ? "top" : "topAndBottom",
        }} filters={{
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            inputs: [<SearchInput autoFocus={true} value={debouncedApiKeyName!} onChange={setDebouncedApiKeyName} accessibleName={"Search API key by purpose"} placeholder={"Search by purpose"}/>],
            advancedFilters: {
                content: (<AdvancedFilters>
                            <AdvancedFilterCheckbox label="Never expire" value={filter.neverExpire} onChange={(neverExpire) => setQueryParams((prev) => ({ ...prev, page: 1, neverExpire }))}/>
                            <AdvancedFilterCheckbox label="Has expired" value={filter.hasExpired} onChange={(hasExpired) => setQueryParams((prev) => ({ ...prev, page: 1, hasExpired }))}/>
                            <>
                                <br />
                                <DeprecatedSelect label="Will expire in days" allowFilter={true} autoFocus={true} allowClear={true} items={[7, 30, 60, 90].map((u) => ({ value: u.toString(), text: `${u} days` }))} onChange={(d) => setQueryParams((prev) => ({ ...prev, page: 1, willExpireInDays: d ? parseFloat(d) : undefined }))} value={filter.willExpireInDays?.toString()}/>
                            </>
                            {users && (<>
                                    <br />
                                    <DeprecatedSelect label="Select a user" allowFilter={true} autoFocus={true} allowClear={true} items={users.map((u) => ({ value: u.Id, text: `${u.Username} (${u.DisplayName})` }))} onChange={(u) => setQueryParams((prev) => ({ ...prev, page: 1, userId: u }))} value={filter.userId}/>
                                </>)}
                        </AdvancedFilters>),
                hasUserSelectedValues: isFiltered,
                onResetFilter: () => setQueryParams(defaultFilter),
            },
        }}>
            {apiKeys.TotalResults === 0 ? (<EmptyStateNoResults />) : (<SimpleDataTable data={apiKeys.Items} columns={[
                {
                    title: "User",
                    columnSize: "small",
                    render: (apiKey) => (<InternalLink to={links.userPage.generateUrl({ userId: apiKey.User.Id })} className={usernameStyles}>
                                    {apiKey.User.DisplayName !== apiKey.User.Username ? `${apiKey.User.DisplayName} (${apiKey.User.Username})` : apiKey.User.DisplayName}
                                </InternalLink>),
                },
                { title: "Purpose", render: (apiKey) => <div className={purposeStyles}>{apiKey.Purpose?.trim()}</div> },
                { title: "Hint", render: (apiKey) => maskKey(apiKey) },
                { title: "Created", render: (apiKey) => <TimeFromNowLabel time={apiKey.Created}/> },
                {
                    title: "Expires",
                    render: (apiKey) => (<ApiKeyExpiryChip apiKey={{
                            Id: apiKey.Id,
                            Purpose: apiKey.Purpose,
                            UserId: apiKey.User.Id,
                            ApiKey: apiKey.ApiKey,
                            Created: apiKey.Created,
                            Expires: apiKey.Expires,
                            Links: apiKey.Links,
                        }}/>),
                },
                {
                    title: "",
                    columnSize: "x-small",
                    render: (apiKey) => <DeprecatedIconButton accessibleName="DeleteApiKey" icon={"TrashIcon"} onClick={() => setApiKeyToDelete(apiKey)}/>,
                },
            ]} getRowKey={(apiKey) => apiKey.Id}/>)}
            {apiKeyToDelete && (<DeleteApiKeyDialog open={!!apiKeyToDelete} onConfirm={async () => {
                if (apiKeyToDelete) {
                    revokeApiKey(apiKeyToDelete);
                    setApiKeyToDelete(undefined);
                }
            }} onClose={() => setApiKeyToDelete(undefined)} apiKeyPurpose={apiKeyToDelete.Purpose} apiKeyUser={apiKeyToDelete.User.DisplayName} apiKeyHint={maskKey(apiKeyToDelete)}/>)}
        </PageContent>);
}
const usernameStyles = css({
    wordBreak: "break-word",
});
const purposeStyles = css({
    wordBreak: "break-word",
});
function useDefaultFilter(): Filter {
    return useMemo(() => {
        return {
            partialPurpose: "",
            neverExpire: false,
            hasExpired: false,
            willExpireInDays: undefined,
            userId: "",
            page: undefined,
            pageSize: undefined,
        };
    }, []);
}
function useFilter(defaultFilter: Filter, queryParams: ApiKeysQueryParams, setQueryParams: QueryParamValuesSetter<ApiKeysQueryParams>): Filter {
    // If there's anything that needs to be adjusted to match the default values, we do it here...
    const filter: Filter = useMemo(() => {
        return {
            ...queryParams,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultFilter, queryParams]);
    // ...If anything has changed after we adjust the filter, we set it back on the URL
    // This causes the URL to unambiguously reflect the current state of the filter
    useEffect(() => {
        if (!isEqual(filter, queryParams))
            setQueryParams(filter);
    }, [queryParams, filter, setQueryParams]);
    // If this component is rendered when the query params have "unspecified" values,
    // then a subsequent render will be fired using a new queryParams object due to the above useEffect.
    // This translates to a new filter object too.
    // If any downstream code relies on this filter object in a dependency array (e.g. to load data), then that hook would
    // be refired (e.g. another api query) without this call to useDeepCompareMemo.
    return useDeepCompareMemo(filter);
}
interface GetPaginatedApiKeysOptions {
    page: number | undefined;
    pageSize: number | undefined;
    partialPurpose: string | undefined;
    neverExpire: boolean | undefined;
    hasExpired: boolean | undefined;
    willExpireInDays: number | undefined;
    userId: string | undefined;
}
async function getPaginatedApiKeys(repository: Repository, { page = 1, pageSize = defaultPageSize, partialPurpose = "", neverExpire = false, hasExpired = false, willExpireInDays = undefined, userId = "" }: GetPaginatedApiKeysOptions) {
    const apiKeys = repository.ApiKeys.listApiKeys({
        skip: (page - 1) * pageSize,
        take: pageSize,
        partialPurpose,
        neverExpire,
        hasExpired,
        willExpireInDays,
        userId,
    });
    // Given we have queryString filters that _may_ exist on the initial request and limit results, this
    // was a compromise to know whether _any_ API keys exist on this instance.
    const apiKeyStats = repository.ApiKeys.listApiKeys({
        skip: 0,
        take: 0,
    });
    return {
        apiKeys: (await apiKeys).ApiKeys,
        hasApiKeys: (await apiKeyStats).ApiKeys.TotalResults > 0,
    };
}
function usePaginatedApiKeysQuery(initialResult: ApiKeysPageLoaderData, queryParams: ApiKeysQueryParams) {
    const { page, pageSize, partialPurpose, neverExpire, hasExpired, willExpireInDays, userId } = queryParams;
    return useQuery((repository) => getPaginatedApiKeys(repository, { page, pageSize, partialPurpose, neverExpire, hasExpired, willExpireInDays, userId }), [page, pageSize, partialPurpose, neverExpire, hasExpired, willExpireInDays, userId], "Get paginated API keys", { initialResult });
}
